import { defineStore } from 'pinia';
import { ref, computed, reactive } from 'vue';
import { useApi } from '@/utils/axios';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ASSISTENCIA_TECNICA_ROTAS } from '@/utils/apiRoutes';

// Constants
const STORAGE_KEY = 'assistenciasColumnVisibility';
const ORDER_STORAGE_KEY = 'assistenciasColumnOrder';
const REFRESH_INTERVAL = 50000; // 50 seconds
const DEFAULT_PAGINATION = { page: 1, pageSize: 20 };

// Helper function to safely parse dates
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  
  // Handle different date formats
  let date;
  if (dateString.includes('T')) {
    date = parseISO(dateString);
  } else {
    // Try to parse the string as a date
    date = new Date(dateString);
  }
  
  return isValid(date) ? date : null;
};

// Extract helper functions for date formatting
const formatDateHelper = (dateString) => {
  if (!dateString) return '';
  
  const date = safeParseDate(dateString);
  if (!date) return dateString; // Return original if parsing failed
  
  try {
    return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString;
  }
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

// Helper function to get a date X days from today in YYYY-MM-DD format
const getDatePlusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

// Helper to create API parameters consistent way
const createApiParams = (filters, columnType, page = 1, pageSize = 20) => {
  const params = { 
    page,
    per_page: pageSize,
    filtro: columnType,
    data_inicial: filters.dataInicio,
    data_final: filters.dataFim,
    nome_fantasia: filters.nomeFantasia,
    telefone: filters.telefoneCliente,
    numero_assistencia: filters.numeroAssistencia,
  };

  if (filters.origem.length > 0) {
    params.origem = filters.origem.join(',');
  }
  
  if (filters.estados.length > 0) {
    params.estados = filters.estados.join(',');
  }
  
  return params;
};

export const useAssistenciaTecnicaStore = defineStore('assistenciaTecnica', () => {
  // State with better organization
  const state = reactive({
    data: null,
    loading: false,
    error: null,
    activeTab: {},
    refreshTimerRef: null,
    lastUpdated: null,
    pagination: { ...DEFAULT_PAGINATION },
    cachedResponses: new Map(), // Cache API responses to reduce duplicate requests
    columnData: {}, // Store data for each column separately
    columnMeta: {}, // Store meta information for each column (pagination data)
    columnLoading: {}, // Track loading state for each column
    isRefreshInProgress: false, // Flag to track if a refresh is in progress
    isComponentMounted: false, // Flag to track if the component is still mounted
  });
  
  // Using ref instead of reactive for arrays to maintain reactivity when items are replaced
  const columns = ref([]);
  
  // Filter state with smarter defaults
  const filtros = ref({
    dataInicio: getTodayDateString(), // Default to today
    dataFim: getDatePlusDays(3),     // Default to today + 3 days
    nomeFantasia: '',
    numeroAssistencia: '',
    telefoneCliente: '',
    origem: [],
    estados: []
  });
  
  // Column types and their API filter values
  const columnTypes = {
    aberta: 'aguardandoaceite',
    andamento: 'emandamento',
    atrasada: 'atrasados',
    concluida: 'concluidos',
    cancelada: 'cancelados',
  };

  // Map de mapeamento inverso: escopo_atual para chave de coluna
  const escopoToColumnKey = {
    'aguardandoaceite': 'aberta',
    'atrasados': 'atrasada',
    'emandamento': 'andamento',
    'concluidos': 'concluida',
    'cancelados': 'cancelada'
  };
  
  // Function to get assistencias by status key with error checking
  const getAssistenciasByStatus = (statusKey) => {
    return state.columnData[statusKey] || [];
  };
  
  // Column visibility computed properties
  const visibleColumns = computed(() => 
    columns.value.filter(column => column.visible));
  
  const hasVisibleColumns = computed(() => 
    columns.value.some(column => column.visible));
  
  const allColumnsVisible = computed({
    get: () => columns.value.every(column => column.visible),
    set: value => toggleAllColumns(value)
  });
  
  // Initialize store with better error handling
  async function initialize() {
    try {
      await fetchQuantidadeAbertas();
      
      setupStatusColumns();
      loadColumnVisibility();
      loadColumnOrder();
      
      return Promise.resolve();
    } catch (error) {
      console.error('Failed to initialize assistência técnica store:', error);
      return Promise.reject(error);
    }
  }
  
  // Setup status columns with default visibility
  function setupStatusColumns() {
    columns.value = [
      { title: 'Abertas', color: '#7FFFD4', key: 'aberta', visible: true },
      { title: 'Em Andamento', color: '#dff0d8', key: 'andamento', visible: true },
      { title: 'Atrasadas', color: '#ffff00', key: 'atrasada', visible: true },
      { title: 'Canceladas', color: '#ff2222', key: 'cancelada', visible: true },
      { title: 'Concluídas', color: '#d9edf7', key: 'concluida', visible: true },
    ];
    
    // Initialize column data and meta
    columns.value.forEach(column => {
      if (!state.columnData[column.key]) {
        state.columnData[column.key] = [];
      }
      if (!state.columnMeta[column.key]) {
        state.columnMeta[column.key] = {
          currentPage: 1,
          total: 0,
          perPage: 20,
          lastPage: 1
        };
      }
      state.columnLoading[column.key] = false;
    });
  }
  
  // Fetch data for all visible columns
  const fetchAssistencias = async (options = {}) => {
    if (!options.silent) {
      state.loading = true;
      state.error = null;
    }
    
    try {
      // Reset column data if not loading more
      if (!options.loadMore) {
        visibleColumns.value.forEach(column => {
          state.columnData[column.key] = [];
          state.columnMeta[column.key] = {
            currentPage: 1,
            total: 0,
            perPage: 20,
            lastPage: 1
          };
        });
      }

      // Verificar se está pesquisando por ID específico
      if (filtros.value.numeroAssistencia && filtros.value.numeroAssistencia.trim() !== '') {
        try {
          // Se pesquisando por ID, busca a assistência específica
          const assistenciaResponse = await useApi(ASSISTENCIA_TECNICA_ROTAS.ASSISTENCIA(filtros.value.numeroAssistencia)).fetch();
          
          if (assistenciaResponse?.data && assistenciaResponse.data.escopo_atual) {
            // Encontra a coluna apropriada com base no status
            const columnKey = escopoToColumnKey[assistenciaResponse.data.escopo_atual] || 'aberta';
            
            // Verifica se a coluna de destino está visível
            const targetColumn = columns.value.find(col => col.key === columnKey);
            
            if (targetColumn && targetColumn.visible) {
              // Coloca a assistência na coluna apropriada
              state.columnData[columnKey] = [assistenciaResponse.data];
              state.columnMeta[columnKey] = {
                currentPage: 1,
                total: 1,
                perPage: 20,
                lastPage: 1
              };
              
              // Limpa outras colunas para mostrar apenas a assistência pesquisada
              visibleColumns.value.forEach(column => {
                if (column.key !== columnKey) {
                  state.columnData[column.key] = [];
                  state.columnMeta[column.key] = {
                    currentPage: 1,
                    total: 0,
                    perPage: 20,
                    lastPage: 1
                  };
                }
              });
              
              // Configura a aba ativa para a assistência
              if (assistenciaResponse.data.id && !state.activeTab[assistenciaResponse.data.id]) {
                state.activeTab[assistenciaResponse.data.id] = 'tabAssistencia';
              }
              
              state.lastUpdated = new Date();
              return { success: true };
            }
          }
          
          // Caso a assistência seja encontrada mas a coluna não esteja visível
          if (assistenciaResponse?.data) {
            state.error = 'Assistência encontrada, mas está em uma coluna que está oculta. Exiba todas as colunas para visualizá-la.';
            
            // Não carregar dados das outras colunas neste caso
            state.lastUpdated = new Date();
            return { success: true, warning: 'column-hidden' };
          }
        } catch (err) {
          console.log("Erro ao pesquisar assistência:", err);
          state.error = 'Assistência não encontrada';
          state.loading = false;
          return { success: false, error: 'Assistência não encontrada' };
        }
      }
      
      // Procede com a pesquisa normal em todas as colunas
      const promises = visibleColumns.value.map(column => 
        fetchColumnData(column.key, options)
      );
      
      await Promise.all(promises);
      state.lastUpdated = new Date();
      
      return { success: true };
    } catch (err) {
      if (!options.silent) {
        state.error = err.message || 'Erro ao carregar assistências técnicas';
      }
    } finally {
      if (!options.silent) {
        state.loading = false;
      }
    }
  };
  
  // Fetch data for a specific column
  const fetchColumnData = async (columnKey, options = {}) => {
    // Don't load more if already at last page
    if (options.loadMore && 
        state.columnMeta[columnKey] && 
        state.columnMeta[columnKey].currentPage >= state.columnMeta[columnKey].lastPage) {
      return [];
    }
    
    // Set column loading state - use a more subtle approach
    // Only set loading to true if this isn't a background refresh
    if (!options.refreshOnly || !state.columnData[columnKey]?.length) {
      state.columnLoading[columnKey] = true;
    }
    
    try {
      const currentPage = options.loadMore ? 
        (state.columnMeta[columnKey]?.currentPage || 1) + 1 : 1;
      
      // Create params using our utility function
      const params = createApiParams(
        filtros.value, 
        columnTypes[columnKey], 
        currentPage,
        state.pagination.pageSize
      );
      
      // Make API request
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.LISTAR_ASSISTENCIAS).fetch({ params });
      
      if (response?.data && response?.meta) {
        // Handle different scenarios based on options
        if (options.loadMore) {
          // Append new data to existing data
          state.columnData[columnKey] = [...state.columnData[columnKey], ...response.data];
        } else {
          // For a fresh load, replace all data
          state.columnData[columnKey] = response.data;
        }
        
        // Store pagination meta information
        state.columnMeta[columnKey] = {
          currentPage: response.meta.current_page,
          total: response.meta.total,
          perPage: response.meta.per_page,
          lastPage: response.meta.last_page
        };
        
        // Setup tabs for new assistencias
        response.data.forEach(assistencia => {
          if (!state.activeTab[assistencia.id]) {
            state.activeTab[assistencia.id] = 'tabAssistencia';
          }
        });
        
        return response.data;
      } else {
        console.error('Resposta da API em formato inesperado para coluna', columnKey);
        return [];
      }
    } catch (err) {
      console.error(`Erro ao carregar coluna ${columnKey}:`, err);
      throw err;
    } finally {
      // Only clear loading if this wasn't a background refresh
      if (!options.refreshOnly || !state.columnData[columnKey]?.length) {
        state.columnLoading[columnKey] = false;
      }
    }
  };
  
  // Method to load more data for a specific column (for infinite scroll)
  const loadMoreForColumn = async (columnKey) => {
    if (!columnKey || state.columnLoading[columnKey]) {
      console.log(`Skipping loadMore for ${columnKey}: already loading or invalid key`);
      return;
    }
    
    if (!state.columnMeta[columnKey] || 
        state.columnMeta[columnKey].currentPage >= state.columnMeta[columnKey].lastPage) {
      console.log(`No more pages to load for ${columnKey}`);
      return;
    }
    
    console.log(`Loading more data for column ${columnKey}: page ${state.columnMeta[columnKey].currentPage + 1}`);
    await fetchColumnData(columnKey, { loadMore: true, silent: true });
  };
  
  // Check if a column has more data to load
  const hasMoreToLoad = (columnKey) => {
    if (!state.columnMeta[columnKey]) return false;
    
    return state.columnMeta[columnKey].currentPage < state.columnMeta[columnKey].lastPage;
  };
  
  // Check if a column is currently loading
  const isColumnLoading = (columnKey) => {
    return state.columnLoading[columnKey] || false;
  };
  
  // Get total count for a column
  const getColumnTotal = (columnKey) => {
    return state.columnMeta[columnKey]?.total || 0;
  };
  
  // Atualizar colunas ativas automaticamente
  const atualizarAssistencias = async () => {
    try {
      
      // Check if any modal is open - if so, skip the update
      if (window.isAnyAssistenciaModalOpen?.()) {
        console.log('Skipping automatic update because a modal is open');
        return;
      }
      
      // Se estiver buscando por ID específico, atualizar apenas essa assistência
      if (filtros.value.numeroAssistencia && filtros.value.numeroAssistencia.trim() !== '') {
        try {
          const assistenciaResponse = await useApi(ASSISTENCIA_TECNICA_ROTAS.ASSISTENCIA(filtros.value.numeroAssistencia)).fetch();
          
          if (assistenciaResponse?.data && assistenciaResponse.data.status) {
            // Encontra a coluna apropriada com base no status
            const columnKey = assistenciaResponse.data.status;
            
            // Verifica se a coluna de destino está visível
            const targetColumn = columns.value.find(col => col.key === columnKey);
            
            if (targetColumn && targetColumn.visible) {
              // Coloca a assistência na coluna apropriada
              state.columnData[columnKey] = [assistenciaResponse.data];
              state.columnMeta[columnKey] = {
                currentPage: 1,
                total: 1,
                perPage: 20,
                lastPage: 1
              };
              
              // Limpa outras colunas para mostrar apenas a assistência pesquisada
              visibleColumns.value.forEach(column => {
                if (column.key !== columnKey) {
                  state.columnData[column.key] = [];
                  state.columnMeta[column.key] = {
                    currentPage: 1,
                    total: 0,
                    perPage: 20,
                    lastPage: 1
                  };
                }
              });
              
              state.lastUpdated = new Date();
              console.log('Atualização da assistência específica concluída');
            }
          }
          
          // Importante: retornar aqui para não continuar com a atualização de todas as colunas
          return;
        } catch (err) {
          console.error('Erro ao atualizar assistência específica:', err);
          // Não continuar com a atualização de todas as colunas
          return;
        }
      }
      
      // Continuar com a atualização normal de todas as colunas
      const promises = visibleColumns.value.map(column => 
        fetchColumnData(column.key, { refreshOnly: true })
      );
      
      await Promise.all(promises);
      
      state.lastUpdated = new Date();
      console.log('Atualização automática concluída');
    } catch (err) {
      console.error('Erro ao atualizar assistências:', err);
    }
  };
  
  // Apply filters with optional pagination reset
  const aplicarFiltros = (resetPage = true) => {
    // Sempre configurar o timer de atualização
    setupRefreshTimer();
    
    fetchAssistencias({ loadMore: false });
  };
  
  // Reset filters - we'll enhance this to be more comprehensive
  const limparFiltros = () => {
    // Reset all filter values to defaults
    filtros.value = {
      dataInicio: getTodayDateString(),
      dataFim: getDatePlusDays(3),
      nomeFantasia: '',
      numeroAssistencia: '',
      telefoneCliente: '',
      origem: [],
      estados: []
    };
    
    // Restart the refresh timer
    setupRefreshTimer();
    
    // Fetch fresh data with reset filters
    return fetchAssistencias({ loadMore: false });
  };
  
  // Column visibility actions
  const toggleAllColumns = (value) => {
    columns.value.forEach(column => {
      column.visible = value;
    });
    saveColumnVisibility();
    // Refetch data for newly visible columns
    fetchAssistencias({ loadMore: false });
  };
  
  // Improved localStorage handling with error recovery
  const saveColumnVisibility = () => {
    try {
      const visibilityState = {};
      columns.value.forEach(column => {
        visibilityState[column.key] = column.visible;
      });
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityState));
    } catch (e) {
      console.error('Failed to save column visibility settings:', e);
    }
  };
  
  const loadColumnVisibility = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (!savedState) return;
      
      const visibilityState = JSON.parse(savedState);
      
      if (columns.value.length > 0) {
        columns.value.forEach(column => {
          if (visibilityState.hasOwnProperty(column.key)) {
            column.visible = Boolean(visibilityState[column.key]);
          }
        });
      }
    } catch (e) {
      console.error('Failed to load column visibility settings:', e);
      // Recover by ensuring at least one column is visible
      if (columns.value.length > 0) {
        columns.value[0].visible = true;
      }
    }
  };
  
  // Improved column order handling
  const saveColumnOrder = () => {
    try {
      const orderState = columns.value.map(column => column.key);
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orderState));
    } catch (e) {
      console.error('Failed to save column order settings:', e);
    }
  };
  
  const loadColumnOrder = () => {
    try {
      const savedOrder = localStorage.getItem(ORDER_STORAGE_KEY);
      if (!savedOrder) return;
      
      const orderState = JSON.parse(savedOrder);
      
      if (Array.isArray(orderState) && orderState.length > 0 && columns.value.length > 0) {
        // Create a map of current columns for faster lookup
        const columnsMap = new Map(
          columns.value.map(col => [col.key, col])
        );
        
        // Create a new array based on the saved order
        const reorderedColumns = [];
        
        // Add columns in the saved order if they exist
        for (const key of orderState) {
          const column = columnsMap.get(key);
          if (column) {
            reorderedColumns.push(column);
            columnsMap.delete(key); // Remove from map to track remaining columns
          }
        }
        
        // Add any remaining columns (new ones since last save)
        for (const column of columnsMap.values()) {
          reorderedColumns.push(column);
        }
        
        // Update if we have all columns
        if (reorderedColumns.length > 0) {
          columns.value = reorderedColumns;
        }
      }
    } catch (e) {
      console.error('Failed to load column order settings:', e);
    }
  };

  // Enhanced refresh timer with better cleanup
  const setupRefreshTimer = () => {
    clearRefreshTimer();
    
    state.isComponentMounted = true;
    
    scheduleNextRefresh();
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
  };
  
  const scheduleNextRefresh = () => {
    if (!state.isComponentMounted) return;
    
    state.refreshTimerRef = setTimeout(async () => {
      if (!state.isRefreshInProgress && state.isComponentMounted) {
        try {
          state.isRefreshInProgress = true;
          await atualizarAssistencias();
        } finally {
          state.isRefreshInProgress = false;
          if (state.isComponentMounted) {
            scheduleNextRefresh();
          }
        }
      } else if (state.isComponentMounted) {
        scheduleNextRefresh();
      }
    }, REFRESH_INTERVAL);
  };
  
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      if (state.refreshTimerRef) {
        clearTimeout(state.refreshTimerRef);
        state.refreshTimerRef = null;
      }
    } else if (document.visibilityState === 'visible' && state.isComponentMounted) {
      if (!state.isRefreshInProgress) {
        atualizarAssistencias().finally(() => {
          state.isRefreshInProgress = false;
          if (!state.refreshTimerRef && state.isComponentMounted) {
            scheduleNextRefresh();
          }
        });
        state.isRefreshInProgress = true;
      }
    }
  };
  
  const clearRefreshTimer = () => {
    // Mark component as unmounted
    state.isComponentMounted = false;
    
    if (state.refreshTimerRef) {
      clearTimeout(state.refreshTimerRef);
      state.refreshTimerRef = null;
    }
    
    // Clean up visibility change listener
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  // Add method to notify "estou na porta"
  const notificarEstouNaPorta = async (assistenciaId) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.NOTIFICAR_ESTOU_NA_PORTA(assistenciaId)).fetch();
      
      return { 
        success: response.status === "success",
        message: 'Notificação "Estou na porta" enviada com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao enviar notificação "Estou na porta":', error);
      return { 
        success: false, 
        message: 'Erro ao enviar notificação "Estou na porta".'
      };
    }
  };

  // Add method to edit assistencia
  const editarAssistencia = async (assistenciaId, data) => {
    if (!assistenciaId) return { success: false, message: 'ID da assistência não informado' };
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.ASSISTENCIA(assistenciaId)).put(data);
      
      // Check response data format
      if (response.status === "success") {
        // Update local state if data is returned
        if (response.data) {
          // Update the assistencia in all column data where it exists
          for (const key in state.columnData) {
            const index = state.columnData[key].findIndex(a => a.id === assistenciaId);
            if (index !== -1) {
              state.columnData[key][index] = { ...state.columnData[key][index], ...response.data };
            }
          }
        }
        
        return { 
          success: true,
          data: response.data
        };
      }
      
      return { 
        success: false, 
        error: response.data
      };
    } catch (error) {
      console.error('Erro ao editar assistência técnica:', error);
      return { 
        success: false, 
        error
      };
    }
  };

  // Add method to update assistencia's observacao
  const atualizarObservacaoAssistencia = async (assistenciaId, observacaoText) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.OBSERVACAO_ASSISTENCIA(assistenciaId)).put({
        observacao: observacaoText
      });
      
      return { 
        success: true,
        message: 'Observação da assistência atualizada com sucesso!',
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao atualizar observação da assistência:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao atualizar observação da assistência',
        error
      };
    }
  };

  // Add method to update assistencia's observacaorevenda
  const atualizarObservacaoRevendaAssistencia = async (assistenciaId, observacaoText) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.OBSERVACAO_ASSISTENCIA_REVENDA(assistenciaId)).put({
        observacao_revenda: observacaoText
      });
      
      return { 
        success: true,
        message: 'Observação da revenda atualizada com sucesso!',
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao atualizar observação da revenda:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao atualizar observação da revenda',
        error
      };
    }
  };

  // Add method for accepting an assistência
  const aceitarAssistencia = async (assistenciaId, payload) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.ACEITAR_ASSISTENCIA(assistenciaId)).post(payload);
      
      // Update data in local store if successful
      if (response.status === "success" && response.data) {
        const sourceColumnKey = findAssistenciaColumn(assistenciaId);
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual] || 'andamento';
        
        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns
          removerAssistenciaDaColuna(sourceColumnKey, assistenciaId);
          adicionarAssistenciaNaColuna(targetColumnKey, response.data);
        }
      }
      
      return { 
        success: response.status === "success",
        message: response.message || 'Assistência aceita com sucesso!',
        data: response.data || null
      };
    } catch (error) {
      console.error('Erro ao aceitar assistência técnica:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao aceitar assistência técnica.'
      };
    }
  };

  // Add method for rejecting an assistência
  const rejeitarAssistencia = async (assistenciaId, payload) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      // Send both idrevenda and motivo in the payload
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.REJEITAR_ASSISTENCIA(assistenciaId)).post(payload);
      
      // Update data in local store if successful
      if (response.status === "success" && response.data) {
        const sourceColumnKey = findAssistenciaColumn(assistenciaId);
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual] || 'cancelada';
        
        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns
          removerAssistenciaDaColuna(sourceColumnKey, assistenciaId);
          adicionarAssistenciaNaColuna(targetColumnKey, response.data);
        }
      }
      
      return { 
        success: response.status === "success",
        message: response.message || 'Assistência rejeitada com sucesso!',
        data: response.data || null
      };
    } catch (error) {
      console.error('Erro ao rejeitar assistência técnica:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao rejeitar assistência técnica.'
      };
    }
  };

  // Add method for marking assistência as "saiu para assistência"
  const sairAssistencia = async (assistenciaId, payload) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.SAIR_ASSISTENCIA(assistenciaId)).post(payload);
      
      // Update data in local store if successful
      if (response.status === "success" && response.data) {
        const sourceColumnKey = findAssistenciaColumn(assistenciaId);
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual] || 'andamento';
        
        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns
          removerAssistenciaDaColuna(sourceColumnKey, assistenciaId);
          adicionarAssistenciaNaColuna(targetColumnKey, response.data);
        }
      }
      
      return { 
        success: response.status === "success",
        message: response.message || 'Assistência marcada como saída com sucesso!',
        data: response.data || null
      };
    } catch (error) {
      console.error('Erro ao marcar assistência como saída:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao marcar assistência como saída.'
      };
    }
  };

  // Add method for marking assistência as delivered
  const assistenciaEntregue = async (assistenciaId, payload) => {
    if (!assistenciaId) {
      return { 
        success: false, 
        message: 'ID da assistência não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.ASSISTENCIA_ENTREGUE(assistenciaId)).post(payload);
      
      // Update data in local store if successful
      if (response.status === "success" && response.data) {
        const sourceColumnKey = findAssistenciaColumn(assistenciaId);
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual] || 'concluida';
        
        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns
          removerAssistenciaDaColuna(sourceColumnKey, assistenciaId);
          adicionarAssistenciaNaColuna(targetColumnKey, response.data);
        }
      }
      
      return { 
        success: response.status === "success",
        message: response.message || 'Assistência marcada como entregue com sucesso!',
        data: response.data || null
      };
    } catch (error) {
      console.error('Erro ao marcar assistência como entregue:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao marcar assistência como entregue.'
      };
    }
  };

  // Helper function to find which column contains a specific assistência
  const findAssistenciaColumn = (assistenciaId) => {
    for (const column of columns.value) {
      const columnKey = column.key;
      if (state.columnData[columnKey]?.some(a => a.id === assistenciaId)) {
        return columnKey;
      }
    }
    return null;
  };

  // Helper to remove assistencia from a column
  const removerAssistenciaDaColuna = (columnKey, assistenciaId) => {
    if (!state.columnData[columnKey]) return;
    
    const index = state.columnData[columnKey].findIndex(a => a.id === assistenciaId);
    if (index !== -1) {
      // Use splice to maintain reactivity
      state.columnData[columnKey].splice(index, 1);
      
      // Update counter if needed
      if (state.columnMeta[columnKey] && state.columnMeta[columnKey].total > 0) {
        state.columnMeta[columnKey].total -= 1;
      }
    }
  };

  // Helper to add assistencia to a column
  const adicionarAssistenciaNaColuna = (columnKey, assistencia) => {
    if (!state.columnData[columnKey]) {
      state.columnData[columnKey] = [];
    }
    
    // Check if assistencia already exists in this column
    const existingIndex = state.columnData[columnKey].findIndex(a => a.id === assistencia.id);
    
    if (existingIndex !== -1) {
      // Update existing assistencia
      state.columnData[columnKey][existingIndex] = assistencia;
    } else {
      // Add new assistencia based on column logic (beginning or end)
      if (columnKey === 'atrasada' || columnKey === 'aberta') {
        state.columnData[columnKey].push(assistencia); // Add to the end
      } else {
        state.columnData[columnKey].unshift(assistencia); // Add to the beginning
      }
      
      // Set up tab for the assistencia if not already set
      if (!state.activeTab[assistencia.id]) {
        state.activeTab[assistencia.id] = 'tabAssistencia';
      }
      
      // Update counter if needed
      if (state.columnMeta[columnKey]) {
        state.columnMeta[columnKey].total += 1;
      }
    }
  };

  // Add method to notify "avisar assistência"
  const avisarAssistencia = async (assistenciaId, revendaId) => {
    if (!assistenciaId || !revendaId) {
      return { 
        success: false, 
        message: 'ID da assistência ou da revenda não informado' 
      };
    }
    
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.AVISAR_ASSISTENCIA(assistenciaId, revendaId)).fetch();
      
      return { 
        success: response.status === "success",
        message: response.message || 'Notificação de assistência enviada com sucesso!'
      };
    } catch (error) {
      console.error('Erro ao avisar assistência:', error);
      return { 
        success: false, 
        message: error.response?.message || 'Erro ao enviar notificação de assistência.'
      };
    }
  };

  // Add method to fetch oldest date data
  const fetchQuantidadeAbertas = async () => {
    try {
      const response = await useApi(ASSISTENCIA_TECNICA_ROTAS.QUANTIDADE_ABERTAS).fetch();
      
      if (response?.status === 'success' && response.data) {
        // If there's an oldest date, update the start date filter
        if (response.data.data_mais_antiga) {
          filtros.value.dataInicio = response.data.data_mais_antiga;
        }
        
        return {
          success: true,
          data: response.data
        };
      }
      
      return { success: false };
    } catch (error) {
      console.error('Erro ao buscar quantidade de assistências abertas:', error);
      return { success: false, error };
    }
  };

  return {
    // State
    get assistencias() { return state.data; },
    get loading() { return state.loading; },
    get error() { return state.error; },
    get activeTab() { return state.activeTab; },
    columns,
    filtros,
    get pagination() { return state.pagination; },
    get lastUpdated() { return state.lastUpdated; },
    
    // Computed
    visibleColumns,
    hasVisibleColumns,
    allColumnsVisible,
    
    // Methods for accessing assistencias by status
    getAssistenciasByStatus,
    
    // Methods for infinite scrolling
    loadMoreForColumn,
    hasMoreToLoad,
    isColumnLoading,
    getColumnTotal,
    
    // Methods - API
    fetchAssistencias,
    fetchColumnData,
    atualizarAssistencias,
    
    // Methods - Filters
    aplicarFiltros,
    limparFiltros,
    
    // Methods - Column visibility
    toggleAllColumns,
    saveColumnVisibility,
    loadColumnVisibility,
    
    // Methods - Column order
    saveColumnOrder,
    loadColumnOrder,
    
    // Methods - Refresh timer
    setupRefreshTimer,
    clearRefreshTimer,
    
    // Methods - Utilities
    formatDate: formatDateHelper,
    
    // Methods - Setup
    initialize,
    setActiveTab: (assistenciaId, tabName) => {
      state.activeTab[assistenciaId] = tabName;
    },
    notificarEstouNaPorta,
    editarAssistencia,
    atualizarObservacaoAssistencia,
    atualizarObservacaoRevendaAssistencia,

    // Add new methods
    aceitarAssistencia,
    rejeitarAssistencia,
    sairAssistencia,
    assistenciaEntregue,
    findAssistenciaColumn,
    removerAssistenciaDaColuna,
    adicionarAssistenciaNaColuna,
    avisarAssistencia,
    fetchQuantidadeAbertas,
  };
});
